import React, { useEffect, useState } from 'react';
import Vendor from 'data/Vendor';
import { useAPI } from 'lib/API';
import { downloadDocument, getCompanyDocuments } from 'requests/documents';
import { renderToast, toast } from 'components/UI/ToastNotification';
import { useAuth } from 'lib/Auth';
import Page from 'components/UI/Page';
import GenericTableWrapper from 'components/UI/GenericTable/GenericTableWrapper';
import initialDocumentsColumns from 'constants/companyDocumentsColumns'
import { mapCompanyDocumentsGridColumns } from 'utils/gridColumnMappings';
import { isVendor } from 'data/User';
import {
  applySortFilter,
  handleOnSortChange
} from 'utils/grid';
import { companyDocumentOptions } from 'constants/documentOptions';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import styled from 'styled-components';
import { Main } from 'styles/themeColors';
import FeatureFlag from 'data/FeatureFlag';
import { BusinessUnit } from 'data/BusinessUnit';

const StyledButton = styled.button<any>`
  background: none;
  border: none;
  color: ${Main.brandDark};
  cursor: pointer;
  padding: 0;
  font-size: inherit;
  margin: 0 5px;
`;

interface Props {
  companyId: string;
  selectedVendor?: Vendor;
  page?: string;
  selectedBusinessUnit?: BusinessUnit;
}

const CompanyDocumentsList = ({
  selectedVendor,
  companyId,
  page,
  selectedBusinessUnit
}: Props) => {
  const { user } = useAuth();
  const isVendorUser = isVendor(user!);
  const [sortBy, setSortBy] = useState(initialDocumentsColumns.find(c => c.defaultSort !== undefined));
  const [currentPage, setCurrentPage] = useState(page ? parseInt(page) : 1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [downloadedDocument, setDownloadedDocument] = useState<any>();
  const [selectedOption, setSelectedOption] = useState('');
  const [documentName, setDocumentName] = useState('');
  const [companyDocuments, setCompanyDocuments] = useState<any>({
    page: 1,
    totalPages: 1,
    size: 1,
    totalItems: 1,
    documents: []
  });

  const calculatePages = (documentsCount: number, pageSize: number) => {
    const pagesCount = Math.ceil(documentsCount / pageSize);

    return (pagesCount === 0) ? 1 : pagesCount;
  };

  const applyPaginationFilter = (companyDocuments: any[], pageIndex: number, pageSize: number) => {
    return companyDocuments.slice((pageIndex - 1) * pageSize, pageIndex * pageSize);
  };

  const onSortChange = (index: number, direction: string) => handleOnSortChange(index, direction, initialDocumentsColumns, setSortBy);

  const onSizeChange = (newSize: number) => {
    setItemsPerPage(newSize);
    setCurrentPage(1)
  }

  const getDocumentsForCompanyReq = useAPI({
    deferFn: getCompanyDocuments,
    onResolve: result => {
      setCompanyDocuments(result);
    },
    onReject: err => {
      renderToast(toast.TYPE.ERROR, err.message);
    }
  });

  const downloadDocumentReq = useAPI({
    deferFn: downloadDocument,
    onResolve: result => {
      setDownloadedDocument(result);
    },
    onReject: err => {
      renderToast(toast.TYPE.ERROR, err.message);
    }
  });

  useEffect(() => {
    const sharedWithCode = selectedBusinessUnit?.brand?.code || selectedVendor?.code;
    if (isVendorUser){
      getDocumentsForCompanyReq.run(companyId, 1, itemsPerPage, 0, selectedVendor?.id, sharedWithCode, user?.email);
    } else {
      getDocumentsForCompanyReq.run(companyId, 1, itemsPerPage, 0, null, sharedWithCode)
    }
  }, []);

  // Create enhanced columns with action buttons
  const enhancedColumns = [...mapCompanyDocumentsGridColumns(initialDocumentsColumns, sortBy), {
    title: 'Actions',
    field: 'actions',
    removable: false,
    sorting: false,
    render: (rowData: any) => (
      <FlexboxContainer>
        {companyDocumentOptions.map((option) => (
          <StyledButton
            key={option.id}
            onClick={() => onSelect(rowData, option.id)}
          >
            {option.title}
          </StyledButton>
        ))}
      </FlexboxContainer>
    )
  }];

  const companiesFromSort = applySortFilter(companyDocuments.documents, sortBy);
  const companyDocumentsFromPagination = applyPaginationFilter(companiesFromSort, currentPage, itemsPerPage);
  const pagesCount = calculatePages(companyDocuments.documents.length, itemsPerPage);

  const getFileName = (name: string, extension: string | null) => {
    const ext = extension ? extension.toLowerCase() : null;
    if (!ext) return name;
    
    const match = name.match(/\.([^./\\]+)$/);
    if (match && match[1].toLowerCase() === ext) return name;
    return `${name}.${ext}`;
  };

  const download = (doc: any, filename: string) => {
    const a = document.createElement('a');

    a.style.display = 'none';
    document.body.appendChild(a);

    const url = window.URL.createObjectURL(doc);

    a.href = url;
    a.download = filename;
    a.click();

    window.URL.revokeObjectURL(url);
  };

  const showInOtherTab = (doc: any) => {
    const url = window.URL.createObjectURL(doc);
    window.open(url, '_blank');
    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (downloadedDocument && downloadedDocument.document) {
      const fileName = getFileName(documentName, downloadedDocument.extension);
      if (selectedOption === 'Preview') {
        if (canBePreviewed(downloadedDocument.extension)) {
          showInOtherTab(downloadedDocument.document);
        } else {
          download(downloadedDocument.document, fileName);
        }
      } else if (selectedOption === 'Download') {
        download(downloadedDocument.document, fileName);
      }

      setSelectedOption('');
    }
  }, [selectedOption, downloadedDocument]);

  const canBePreviewed: (ext: string | null) => boolean = (ext: string | null) => {
    return ext !== null && ['jpg', 'png', 'pdf', 'jpeg', 'tiff'].includes(ext);
  };

  const onSelect = (data: any, optionId: number) => {
    const selectedOption = companyDocumentOptions.find(item => item.id === optionId);

    if (selectedOption) {
      setSelectedOption(selectedOption.title);
      setDocumentName(data.name);

      downloadDocumentReq.run(data.id, data.url, data.mimeType);
    }
  }

  return (
    <Page padding='0'>
      <FlexboxContainer>
        <GenericTableWrapper
          columns={enhancedColumns}
          data={companyDocumentsFromPagination}
          isLoading={getDocumentsForCompanyReq.isLoading}
          toolbar
          overrideToolbar
          onOrderChange={onSortChange}
          wrapperWidth="100%"
          customPagination={{
            itemsPerPage,
            setItemsPerPage: onSizeChange,
            currentPage,
            setCurrentPage,
            totalPages: pagesCount,
            shouldRedirect: false,
            baseRoute: ''
          }}
          sortBy={sortBy}
        />
      </FlexboxContainer>
    </Page>
    );
};

export default CompanyDocumentsList;
